/**
 * CookieBanner.js
 * Banner de consentimiento de cookies conforme a la Guía de la AEPD (2023).
 *
 * Requisitos que cumple:
 *  - No se carga nada de analítica hasta que hay decisión (ver lib/consent.js).
 *  - "Rechazar" tiene la MISMA jerarquía visual que "Aceptar" (mismo tamaño,
 *    contraste equivalente). No vale esconderlo en un segundo nivel.
 *  - Seguir navegando o cerrar NO equivale a aceptar: el banner no es
 *    descartable sin elegir.
 *  - Casillas no premarcadas en el panel de preferencias.
 *  - La decisión es revocable en todo momento desde /cookies.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { hasDecided, saveConsent } from '../lib/consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  // No premarcada: el usuario debe activarla conscientemente.
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    // Se evalúa tras montar para no bloquear el primer render.
    if (!hasDecided()) setVisible(true);
  }, []);

  // El portal nativo y el banner no deben convivir con el splash inicial.
  if (!visible) return null;

  const decide = (choices) => {
    saveConsent(choices);
    setVisible(false);
  };

  return (
    <div style={S.overlay} role="dialog" aria-modal="true" aria-labelledby="cookie-title">
      <div style={S.panel}>
        <div style={S.header}>
          <Cookie size={18} style={{ color: 'var(--primary)', flexShrink: 0 }} />
          <h2 id="cookie-title" style={S.title}>Cookies y privacidad</h2>
        </div>

        {!showPrefs ? (
          <>
            <p style={S.text}>
              Usamos almacenamiento propio necesario para mantener tu sesión iniciada,
              recordar tu equipo y tu preferencia de tema. Estas no se pueden desactivar.
            </p>
            <p style={S.text}>
              Además, si nos das permiso, usamos <strong>PostHog</strong> para entender
              cómo se usa la aplicación y mejorarla. Implica una transferencia de datos
              a Estados Unidos. Puedes rechazarlo sin perder ninguna funcionalidad.
            </p>
            <p style={S.links}>
              Más detalle en la{' '}
              <Link to="/cookies" style={S.link}>política de cookies</Link> y en la{' '}
              <Link to="/privacidad" style={S.link}>política de privacidad</Link>.
            </p>

            <div style={S.actions}>
              {/* Rechazar y Aceptar comparten estilo: exigencia de la AEPD. */}
              <button style={{ ...S.btn, ...S.btnNeutral }} onClick={() => decide({ analytics: false })}>
                Rechazar
              </button>
              <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => decide({ analytics: true })}>
                Aceptar
              </button>
            </div>
            <button style={S.btnLink} onClick={() => setShowPrefs(true)}>
              Configurar preferencias
            </button>
          </>
        ) : (
          <>
            <button style={S.close} onClick={() => setShowPrefs(false)} aria-label="Volver">
              <X size={16} />
            </button>

            <div style={S.row}>
              <div style={S.rowMain}>
                <span style={S.rowTitle}>Necesarias</span>
                <span style={S.rowDesc}>
                  Sesión, autenticación, equipo activo y tema. Imprescindibles para
                  que la aplicación funcione.
                </span>
              </div>
              <span style={S.always}>Siempre activas</span>
            </div>

            <div style={S.row}>
              <div style={S.rowMain}>
                <span style={S.rowTitle}>Analíticas</span>
                <span style={S.rowDesc}>
                  PostHog. Métricas de uso agregadas para mejorar el producto.
                  Transferencia a EE. UU. La grabación de sesión está desactivada.
                </span>
              </div>
              <label style={S.switchWrap}>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  style={S.checkbox}
                />
              </label>
            </div>

            <div style={S.actions}>
              <button style={{ ...S.btn, ...S.btnNeutral }} onClick={() => decide({ analytics: false })}>
                Rechazar todo
              </button>
              <button style={{ ...S.btn, ...S.btnPrimary }} onClick={() => decide({ analytics })}>
                Guardar selección
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 9999,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    padding: '1rem', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
  },
  panel: {
    position: 'relative',
    width: '100%', maxWidth: 520,
    background: 'var(--surface-1, #161E18)',
    border: '1px solid var(--border-strong, rgba(255,255,255,0.10))',
    borderRadius: 14,
    boxShadow: 'var(--shadow-lg, 0 20px 50px rgba(0,0,0,0.5))',
    padding: '1.15rem',
    maxHeight: '85vh', overflowY: 'auto',
  },
  header: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.7rem' },
  title: { fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-0, #f0f2f5)', margin: 0 },
  text: {
    fontSize: '0.8rem', lineHeight: 1.55,
    color: 'var(--text-1, rgba(240,242,245,0.78))', margin: '0 0 0.6rem',
  },
  links: { fontSize: '0.75rem', color: 'var(--text-2, rgba(240,242,245,0.52))', margin: '0 0 0.9rem' },
  link: { color: 'var(--primary, #10B981)', textDecoration: 'none' },
  actions: { display: 'flex', gap: '0.55rem', marginTop: '0.3rem' },
  btn: {
    flex: 1, padding: '0.6rem 0.9rem', borderRadius: 9,
    fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer',
    border: '1px solid transparent', transition: 'opacity 0.15s',
  },
  btnPrimary: { background: 'var(--primary, #10B981)', color: 'var(--primary-ink, #03150E)' },
  btnNeutral: {
    background: 'var(--surface-2, #1A231C)',
    color: 'var(--text-0, #f0f2f5)',
    borderColor: 'var(--border-strong, rgba(255,255,255,0.10))',
  },
  btnLink: {
    display: 'block', width: '100%', marginTop: '0.6rem',
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '0.75rem', color: 'var(--text-2, rgba(240,242,245,0.52))',
    textDecoration: 'underline', padding: '0.25rem',
  },
  close: {
    position: 'absolute', top: 10, right: 10,
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'var(--text-2, rgba(240,242,245,0.52))', padding: 4,
  },
  row: {
    display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
    padding: '0.75rem 0',
    borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.055))',
  },
  rowMain: { display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1 },
  rowTitle: { fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-0, #f0f2f5)' },
  rowDesc: { fontSize: '0.72rem', lineHeight: 1.5, color: 'var(--text-2, rgba(240,242,245,0.52))' },
  always: {
    fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap',
    color: 'var(--primary, #10B981)', paddingTop: 2,
  },
  switchWrap: { display: 'flex', alignItems: 'center', paddingTop: 2 },
  checkbox: { width: 18, height: 18, accentColor: 'var(--primary, #10B981)', cursor: 'pointer' },
};
